import { Fade, Grid, Typography, useMediaQuery } from "@mui/material"
import moment from "moment"
import React, { useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Button, StyledPaper } from "../components"
import StatsBox, { StatsBoxType } from "../components/commons/StatsBox"
import { TokenDetailsInputForm, TokenDetailsNav } from "../components/token-details"
import InputFormStats from "../components/token-details/InputFormStats"
import useGlobalStore, { MarketData } from "../stores/global"

const statsBoxPlaceHolder: StatsBoxType[] = [
  { title: 'Total Deposited', content: '0', type: 'dollar' },
  { title: 'Available Liquidity', content: '0', type: 'dollar' },
  { title: 'Utilization Rate', content: '0', type: 'percent' },
  { title: 'Oracle Price', content: '0', type: 'dollar' },
]

const TokenDetails = () => {
  const isSmallerScreen = useMediaQuery((theme: any) => theme.breakpoints.down('lg'))
  const markets = useGlobalStore((state) => state.markets)
  const navigate = useNavigate()
  const params = useParams()
  const asset = params?.denom?.toUpperCase() ?? ''
  // const assetInfo = tokenMapping[asset]
  const market = markets.find((market) => market.asset.toUpperCase() === asset.toUpperCase())
  // const isMarketLoading = useGlobalStore((state) => state.marketsLoading)
  // if (isMarketLoading || !markets.length) return <LinearProgress />
  if (!market) {
    navigate('/')
    return null
  }
  // return <LinearProgress />  //market not found; TODO: add a not found page

  const statsBox: StatsBoxType[] = market ? statsBoxPlaceHolder.map((stats) => {
    let content: number | string = '0'
    let tooltip: string | undefined
    switch (stats.title) {
      case 'Total Deposited':
        content = market.totalSupplied * market.price
        tooltip = `The total amount of ${market.asset} deposited into Intersect.`
        break;
      case 'Available Liquidity':
        content = (market.totalSupplied * market.price) - (market.totalBorrowed * market.price)
        tooltip = `The total amount of ${market.asset} currently available for borrowing.`
        break;
      case 'Utilization Rate':
        content = market.utilizationRate
        tooltip = 'The ratio of borrowed funds to total available liquidity. It shows how much of the available funds are currently in use.'
        break;
      case 'Oracle Price':
        content = market.price
        if (market.price < 0.1) stats.minDecimals = 4
        break;
    }
    return { ...stats, content: content, tooltip: tooltip }
  }) : statsBoxPlaceHolder

  return (
    <Grid container rowGap={{ xs: 1, sm: 4 }}>
      {!isSmallerScreen && <TokenDetailsNav />}
      <Grid item container gap={3} flexWrap={'nowrap'}>
        <Grid id='asset-stats' item container spacing={{ xs: 1, sm: 3 }} xs={12} lg={7}>
          <Grid item container gap={{ xs: 1.5, sm: 3 }} flexDirection={'column'}>
            <Grid item container spacing={{ xs: 1, sm: 3 }}>
              {
                statsBox.map((stats, index) => {
                  return <Fade in={true} timeout={400 * (index + 1) > 1200 ? 1200 : 400 * (index + 1)}>
                    <Grid item container width={'100%'} xs={6} sm={3} key={stats.title}>
                      <StatsBox key={stats.title} {...stats} style={{ minWidth: '0rem' }} />
                    </Grid>
                  </Fade>
                }
                )
              }
            </Grid>
            <Fade in timeout={800}>
              <Grid item>
                <TokenOverviewChart assetAddress={market?.contractAddress} />
              </Grid>
            </Fade>
            <Fade in timeout={400}>
              <Grid item>
                <TokenStatsInfo market={market} />
              </Grid>
            </Fade>
            <Fade in timeout={400}>
              <Grid item>
                <TokenUtilizationChart assetAddress={market?.contractAddress} utilizationRate={market.utilizationRate} />
              </Grid>
            </Fade>
          </Grid>
        </Grid>
        <Fade in timeout={1200}>
          <Grid id='asset-form' item
            container sx={{ display: { xs: 'none', lg: 'flex' }, maxHeight: '25.8rem' }}
            xs={0} lg={5} gap={3}
          >
            <TokenDetailsInputForm />
            <InputFormStats />
          </Grid>
        </Fade>
      </Grid >
    </Grid >
  )
}

const TokenUtilizationChart = (props: { assetAddress: `0x${string}`, utilizationRate: number }) => {
  const { assetAddress, utilizationRate } = props
  const getUtilizationChart = useGlobalStore((state) => state.getUtilizationChart)
  // const utilizationRateChartLoading = useGlobalStore((state) => state.utilizationRateChartLoading)
  const [chartData, setChartData] = React.useState([])
  const [optimalUsageRatio, setOptimalUsageRatio] = React.useState(0)

  useEffect(() => {
    const getChartData = async () => {
      const getChartData = await getUtilizationChart(assetAddress)
      setChartData(getChartData.chartData)
      setOptimalUsageRatio(getChartData.optimalUsageRatio)
    }
    getChartData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <Grid container>
      <StyledPaper color='primary' sx={{ padding: '2rem 2rem', width: '100%', minHeight: '33.5dvh' }}>
        <Grid item container xs={12} flexDirection={'row'} flexWrap={'nowrap'} sx={{ width: '100%' }}>
          <Grid item xs={5}>
            <Typography variant='xl' color='text.primary' noWrap>
              Interest Rate & Utilization
            </Typography>
          </Grid>
        </Grid>
        <Grid item sx={{ width: '100%', padding: '1rem 0.5rem 0rem 0.5rem' }}>
          <ResponsiveContainer width={'100%'} height={350}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <ReferenceLine stroke="#23FFB0" strokeDasharray="3 3"
                label={{ position: 'top', value: `Optimal ${optimalUsageRatio}%`, fill: '#23FFB0', offset: 10 }}
                color="#23FFB0"
                isFront={true}
                segment={[{ x: `${optimalUsageRatio}%`, y: 0 }, { x: `${optimalUsageRatio}%`, y: 77 }]}
              />
              <ReferenceLine stroke="#FFFFFF" strokeDasharray="3 3"
                label={{ position: 'top', value: `Current ${utilizationRate.toFixed(2)}%`, fill: '#FFFFFF', offset: 10 }}
                color="#FFFFFF"
                isFront={true}
                segment={[{ x: `${Math.round(utilizationRate * 2) / 2}%`, y: 0 }, { x: `${Math.round(utilizationRate * 2) / 2}%`, y: 200 }]}
              />
              <Line unit="%" name="Supply APY" dataKey="supplyApy" stroke="#5292ED" strokeWidth={2} legendType='circle' dot={false} />
              <Line unit="%" name="Borrow APY" dataKey="borrowApy" stroke="#65E3FF" strokeWidth={2} legendType='circle' dot={false} />
              <CartesianGrid stroke="rgb(255,255,255,0.12)" vertical={false} />
              <CartesianGrid x={10} y={20} stroke="rgb(255,255,255,0.12)" horizontal={false} strokeDasharray="3 3" />
              <XAxis interval={19} label={"Utilisation Rate"} axisLine={false} dataKey="xAxisName" dy={20} />
              <YAxis unit={"%"} type="number" axisLine={false} dx={-20} />
              <Tooltip contentStyle={{ backgroundColor: 'rgb(255,255,255,0.12)' }} />
              <Legend verticalAlign="bottom" height={0}
                wrapperStyle={{
                  // position: 'relative',
                  boxSizing: 'border-box',
                  paddingTop: '40px',
                }} />
            </LineChart>
          </ResponsiveContainer>
        </Grid>
      </StyledPaper >
    </Grid>
  )
}


const TokenOverviewChart = (props: { assetAddress: `0x${string}` }) => {
  const [chartData, setChartData] = React.useState([])
  const [selectedTab, setSelectedTab] = React.useState(7)
  const { assetAddress } = props
  const getMarketOverviewChart = useGlobalStore((state) => state.getMarketOverviewChart)


  useEffect(() => {
    const getChartData = async () => {
      const until = moment().unix()
      const from = moment().subtract(selectedTab, 'days').unix()
      const chartDataQuery = await getMarketOverviewChart(assetAddress, from, until)
      setChartData(chartDataQuery)
    }
    getChartData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])
  return (
    <Grid container>
      <StyledPaper color='primary' sx={{ padding: '2rem 2rem', width: '100%', minHeight: '33.5dvh' }}>
        <Grid item container xs={12} flexDirection={'row'} sx={{ width: '100%' }} spacing={2}>
          <Grid item xs={12} sm={5}>
            <Typography variant='xl' color='text.primary'>
              Market Overview
            </Typography>
          </Grid>
          <Grid item container gap={{ xs: 0, sm: 2 }} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} xs={12} sm={7}>
            <Button color='primary' onClick={() => setSelectedTab(7)} variant={selectedTab === 7 ? 'contained' : 'text'} sx={{ padding: '0rem', minWidth: '4rem' }}>
              <Typography variant="s" color={selectedTab === 7 ? 'text.primary' : 'text.secondary'}>
                7D
              </Typography>
            </Button>
            <Button color='primary' onClick={() => setSelectedTab(30)} variant={selectedTab === 30 ? 'contained' : 'text'} sx={{ padding: '0rem', minWidth: '4rem' }}>
              <Typography variant="s" color={selectedTab === 30 ? 'text.primary' : 'text.secondary'}>
                30D
              </Typography>
            </Button>
            <Button color='primary' onClick={() => setSelectedTab(90)} variant={selectedTab === 90 ? 'contained' : 'text'} sx={{ padding: '0rem', minWidth: '4rem' }}>
              <Typography variant="s" color={selectedTab === 90 ? 'text.primary' : 'text.secondary'}>
                90D
              </Typography>
            </Button>
            <Button color='primary' onClick={() => setSelectedTab(180)} variant={selectedTab === 180 ? 'contained' : 'text'} sx={{ padding: '0rem', minWidth: '4rem' }}>
              <Typography variant="s" color={selectedTab === 180 ? 'text.primary' : 'text.secondary'}>
                180D
              </Typography>
            </Button>
          </Grid>
        </Grid>
        <Grid item sx={{ width: '100%', padding: '1rem 0.5rem 0rem 0.5rem' }}>
          <ResponsiveContainer width={'100%'} height={350}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              {/* <Line yAxisId="left" name="Net Supplied" dataKey="net_supplied" stroke="#5292ED" legendType='circle' dot={false} />
              <Line yAxisId="left" name="Net Borrowed" dataKey="net_borrowed" stroke="#A965FF" legendType='circle' dot={false} /> */}
              <Line unit="%" yAxisId="right" name="Borrow APY" dataKey="borrow_apy" stroke="#F152FF" legendType='circle' dot={false} />
              <Line unit="%" yAxisId="right" name="Supply APY" dataKey="supply_apy" stroke="#23FFB0" legendType='circle' dot={false} />
              {/* <Line dataKey="pv" stroke="#ff0ff0" legendType='circle' />
              <Line dataKey="amt" stroke="#A965FF" legendType='circle' /> */}
              <CartesianGrid stroke="rgb(255,255,255,0.12)" vertical={false} />
              <XAxis tickFormatter={(label) => {
                return moment(label).format('DD-MM HH:mm')
              }} angle={0} textAnchor="center" interval={
                selectedTab === 7 ? 70 : selectedTab === 30 ? 200 : selectedTab === 90 ? 450 : 900
              } axisLine={false} dataKey="date" dy={20} />
              <YAxis yAxisId="left" type="number" axisLine={false} dx={-20} />
              <YAxis unit="%" yAxisId="right" orientation="right" />
              <Tooltip contentStyle={{ backgroundColor: 'rgb(255,255,255,0.12)' }} />
              <Legend verticalAlign="bottom" height={0}
                wrapperStyle={{
                  // position: 'relative',
                  boxSizing: 'border-box',
                  paddingTop: '40px',
                }} />
            </LineChart>
          </ResponsiveContainer>
        </Grid>
      </StyledPaper >
    </Grid >
  )
}

const formStatsSupply: StatsBoxType[] = [
  { title: 'Total Deposited', content: '0', subContent: '0', type: 'number', progressWheel: 0, minDecimals: 0, subContentType: 'dollar' },
  { title: 'Deposit Capacity', content: '0', subContent: '0', type: 'dollar' },
  { title: 'Deposit APY', content: '0', subContent: '0', type: 'percent' },
]

const formStatsBorrow: StatsBoxType[] = [
  { title: 'Total Borrowed', content: '0', subContent: '0', type: 'number', progressWheel: 0, minDecimals: 0, subContentType: 'dollar' },
  { title: 'Borrow Capacity', content: '0', subContent: '0', type: 'dollar' },
  { title: 'Borrow APY', content: '0', subContent: '0', type: 'percent' },
]

const formStatsLiquidation: StatsBoxType[] = [
  { title: 'Max LTV', content: '0', type: 'percent', tooltip: 'The maximum loan-to-value ratio allowed for borrowing against your collateral. It represents the upper limit on how much you can borrow relative to your collateral.' },
  { title: 'Liquidation Threshold', content: '0', type: 'percent', tooltip: 'The collateral value level at which your position is at risk of liquidation. If your collateral falls below this threshold, it may be liquidated to repay your loan.' },
  { title: 'Liquidation Bonus', content: '0', type: 'percent', tooltip: 'The additional fee or discount applied during liquidation. It represents the incentive or penalty for liquidating collateral to cover outstanding loans.' },
]

const TokenStatsInfo = (props: { market: MarketData }) => {
  const { market } = props
  const [selectedTab, setSelectedTab] = React.useState(0)
  const [apy, setApy] = React.useState({ avg30DaysBorrowApy: 0, avg30DaysSupplyApy: 0 })
  const get30DayAvgApy = useGlobalStore((state) => state.get30DayAvgApy)
  const { avg30DaysBorrowApy, avg30DaysSupplyApy } = apy

  useEffect(() => {
    const from = moment().subtract(30, 'days').unix()
    const until = moment().unix()
    const get30DayAvgApyFn = async () => {
      const apy = await get30DayAvgApy(market?.contractAddress, from, until)
      setApy(apy)
    }
    get30DayAvgApyFn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectTab = (tab: number) => {
    setSelectedTab(tab)
  }

  const formStatsPlaceHolder = selectedTab === 0 ? formStatsSupply : selectedTab === 1 ? formStatsBorrow : formStatsLiquidation

  const formStats = formStatsPlaceHolder.map((stats) => {
    if (selectedTab === 0) {
      switch (stats.title) {
        case 'Total Deposited':
          stats.content = market.totalSupplied
          stats.subContent = market.totalSupplied * market.price
          stats.progressWheel = (market.totalSupplied && market.totalSuppliedCap) ?
            (market.totalSupplied / market.totalSuppliedCap) * 100 : 0.00001
          break
        case 'Deposit Capacity':
          stats.content = market.totalSuppliedCap ? market.totalSuppliedCap : '∞'
          stats.type = market.totalSuppliedCap ? 'dollar' : 'string'
          stats.subContent = market.totalSuppliedCap ? (market.totalSuppliedCap * market.price) : '∞'
          stats.minDecimals = 2
          break
        case 'Deposit APY':
          stats.content = `${market.supplyApy.toFixed(2)}%`
          stats.subContent = `${avg30DaysSupplyApy.toFixed(2)}% 30D avg`
          stats.type = 'string'
          break
      }
    } else if (selectedTab === 1) {
      const borrowMaxCap = market.totalBorrowedCap //Math.min(market.totalBorrowedCap, market.totalSupplied)
      switch (stats.title) {
        case 'Total Borrowed':
          stats.content = market.totalBorrowed
          stats.subContent = market.totalBorrowed * market.price
          stats.progressWheel = (market.totalBorrowed && borrowMaxCap) ?
            (market.totalBorrowed / borrowMaxCap) * 100 : 0.00001
          break
        case 'Borrow Capacity':
          stats.content = borrowMaxCap ? borrowMaxCap : '∞'
          stats.type = 'number'
          stats.subContentType = 'dollar'
          stats.subContent = borrowMaxCap ? (borrowMaxCap * market.price) : '∞'
          stats.minDecimals = 2
          break
        case 'Borrow APY':
          stats.content = `${market.borrowApy.toFixed(2)}%`
          stats.subContent = `${avg30DaysBorrowApy.toFixed(2)}% 30D avg`
          stats.type = 'string'
          break
      }
    } else if (selectedTab === 2) {
      switch (stats.title) {
        case 'Max LTV':
          stats.content = market.ltv ?? 0
          break
        case 'Liquidation Threshold':
          stats.content = market.liquidationThreshold ?? 0
          break
        case 'Liquidation Bonus':
          stats.content = market.liquidationBonus ?? 0
          break
      }
    }
    return { ...stats }
  })
  return (
    <StyledPaper color='primary' sx={{ padding: { xs: '1.5rem 1rem', sm: '1.5rem 2rem' }, width: '100%', overflow: 'auto', boxSizing: 'border-box' }}>
      <Grid container item style={{}} spacing={2}>
        <Grid id='tokenStatsInfo-title' item container gap={4}
          flexWrap={'nowrap'}
          sx={{ cursor: 'pointer', width: { xs: '29rem', sm: '100%' }, maxWidth: '79dvw', overflow: 'auto', margin: '0rem 0rem' }}>
          <Grid item onClick={() => selectTab(0)} sx={{ cursor: 'pointer', zIndex: 99 }}>
            <Typography
              sx={{ cursor: 'pointer', minWidth: { xs: '11rem', sm: '0px' } }}
              noWrap
              textOverflow={'unset'}
              variant='xl'
              color={selectedTab === 0 ? 'text.primary' : 'text.secondary'}>
              Deposit Info
            </Typography>
          </Grid>
          <Grid item onClick={() => selectTab(1)} sx={{ cursor: 'pointer', zIndex: 99 }}>
            <Typography
              sx={{ cursor: 'pointer', minWidth: { xs: '11rem', sm: '0px' } }}
              noWrap
              textOverflow={'unset'}
              variant='xl' color={selectedTab === 1 ? 'text.primary' : 'text.secondary'} >
              Borrow Info
            </Typography>
          </Grid>
          <Grid item onClick={() => selectTab(2)} sx={{ cursor: 'pointer', zIndex: 99 }}>
            <Typography
              sx={{ cursor: 'pointer', minWidth: { xs: '11rem', sm: '0px' } }}
              noWrap
              textOverflow={'unset'}
              variant='xl' color={selectedTab === 2 ? 'text.primary' : 'text.secondary'}>
              Liquidation Info
            </Typography>
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          {
            formStats.map((stats) => {
              return <Grid item container width={'100%'} key={stats.title} sm={12} md={4} >
                <StatsBox key={stats.title} {...stats} style={{ width: '100%', maxWidth: { xs: '100%' } }} />
              </Grid>
            }
            )
          }
        </Grid>
      </Grid>
    </StyledPaper >
  )
}

export default TokenDetails